import React, { useEffect, useState } from "react";
import { pipe } from "fp-ts/lib/function";
import { array, either } from "fp-ts";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMPageWrap } from "./../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMPageBody } from "./../../components/Simple/CRMPageBody/CRMPageBody";
import { CRMLegalMenuBarContainer } from "./../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import { CRMSpacingRow } from "../../components/CRMSpacingRow/CRMSpacingRow";
import { SimpleGrid } from "../../components/BuildingBlocks/SimpleGrid";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { Sticky } from "../../components/BuildingBlocks/Sticky";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMCardOutside } from "../../components/CRMCardOutside/CRMCardOutside";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { FontQuicksand } from "../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../components/BuildingBlocks/FontSize";
import { TextColor } from "../../components/BuildingBlocks/TextColor";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMIcon } from "../../components/CRMIcon/CRMIcon";
import { CRMFlatInputGeneral } from "../../components/CRMFlatInputGeneral/CRMFlatInputGeneral";
import { CRMButtonTertiary } from "../../components/CRMButtonTertiary/CRMButtonTertiary";
import { CRMProgressBar } from "../../components/Simple/CRMProgressBar/CRMProgressBar";
import CRMTextAreaComponent from "../../components/CRMTextAreaComponent/CRMTextAreaComponent";
import { TLegalMIReportType, getReportTitleAndDescription } from "../../../../domain/codecs/LegalMIReportType";
import { CLCAuditSummary, TCLCAuditSummary } from "../../../../domain/codecs/CLCAuditSummary";
import { CLCAuditDocumentLink, CLCAuditDocumentLinks } from "../../../../domain/codecs/CLCAuditDocumentLink";

const INSPECTION_DATE_ISO = "2026-07-22";

const REPORT_GROUPS: Array<{ area: string; blurb: string; reports: Array<TLegalMIReportType> }> = [
    { area: "AML / CTF", blurb: "ID&V, risk grading and source-of-wealth evidence.", reports: ["id-checks-report", "risk-report", "source-of-wealth-report"] },
    { area: "Conflicts of Interest & Dual Representation", blurb: "Conflicts identified/resolved and every acting-on-both-sides matter.", reports: ["coi-report", "dual-rep-report"] },
    { area: "Complaints", blurb: "Formal complaints raised and any Legal Ombudsman referral.", reports: ["complaints-report"] },
    { area: "Referral Arrangements", blurb: "Introducer referral agreements, review dates and per-case referral status.", reports: ["referral-agreements-report", "legal-referrals-report", "property-referrals-report"] },
    { area: "Accounts, Fees & Undertakings", blurb: "Fees, late payments, search ledger, bank-account changes and undertakings.", reports: ["fees-report", "late-payment-report", "search-ledger-report", "bank-account-changes-report", "undertakings-report"] },
    { area: "Case Management & Post-Completion", blurb: "Open/instructed files, completions, audit, milestone timings and post-completion.", reports: ["new-cases-report", "completion-report", "case-audit-report", "milestones-report", "time-between-milestones-report", "post-completion-report", "bsa-report"] },
];

type TDocLink =
    | { kind: "report"; report: TLegalMIReportType }
    | { kind: "guide"; slug: string }
    | { kind: "external"; key: string }
    | { kind: "none" };

type TDocRow = { ref: string; document: string; link: TDocLink; notes: string };

const PART_C_DOCS: Array<TDocRow> = [
    { ref: "1", document: "Completed inspection questionnaire", link: { kind: "none" }, notes: "Several sections auto-answerable from the reports" },
    { ref: "2", document: "Org structure chart (all staff)", link: { kind: "external", key: "orgChart" }, notes: "HR / ops maintained" },
    { ref: "3", document: "AML policy & procedures", link: { kind: "guide", slug: "aml-policy" }, notes: "Published in Guides" },
    { ref: "4", document: "Practice-Wide Risk Assessment (MLR 2017)", link: { kind: "external", key: "pwra" }, notes: "Compliance — note last-reviewed date" },
    { ref: "5", document: "Client/matter risk assessment (CMRA)", link: { kind: "report", report: "risk-report" }, notes: "Per-case grade in the Risk Report" },
    { ref: "6", document: "Source-of-funds / source-of-wealth", link: { kind: "report", report: "source-of-wealth-report" }, notes: "Per-client status in the Source of Wealth Report" },
    { ref: "7", document: "SARs log (ISARs + DAML)", link: { kind: "external", key: "sarsLog" }, notes: "Maintained manually (likely nil return)" },
    { ref: "8", document: "MLRO & DMLRO training log", link: { kind: "external", key: "mlroTrainingLog" }, notes: "Compliance training records" },
    { ref: "9", document: "All-staff AML training log", link: { kind: "external", key: "amlTrainingLog" }, notes: "Compliance training records" },
    { ref: "10", document: "Complaints policy / register", link: { kind: "external", key: "complaintsRegister" }, notes: "Compliance — maintained externally" },
    { ref: "11", document: "Complaints log/record", link: { kind: "report", report: "complaints-report" }, notes: "Complaints Report — complaint + Ombudsman dates" },
    { ref: "12", document: "Conflicts of Interest policy", link: { kind: "guide", slug: "conflicts-of-interest-policy" }, notes: "Published in Guides" },
    { ref: "13", document: "Referral agreements (copies)", link: { kind: "report", report: "referral-agreements-report" }, notes: "Referral Agreements Report — on file + review status" },
    { ref: "14", document: "Business Continuity Plan", link: { kind: "external", key: "businessContinuityPlan" }, notes: "Ops" },
    { ref: "15", document: "Risk register", link: { kind: "external", key: "riskRegister" }, notes: "Compliance / ops" },
    { ref: "16", document: "Terms & Conditions of Business", link: { kind: "external", key: "termsOfBusiness" }, notes: "Standard client doc" },
    { ref: "17", document: "Blank client care letter / TOE", link: { kind: "external", key: "clientCareLetter" }, notes: "Standard client doc" },
    { ref: "18", document: "Blank fee estimate", link: { kind: "external", key: "feeEstimateSample" }, notes: "System generates quotes; sample doc from compliance" },
    { ref: "19", document: "Standard client-facing brochures", link: { kind: "external", key: "clientBrochures" }, notes: "Marketing / onboarding pack" },
    { ref: "20", document: "Standard post-completion doc to client", link: { kind: "external", key: "postCompletionDoc" }, notes: "Standard client doc" },
    { ref: "21", document: "Most recent PII proposal", link: { kind: "external", key: "piiProposal" }, notes: "Broker / finance" },
    { ref: "22", document: "Current PII policy evidence", link: { kind: "external", key: "piiPolicy" }, notes: "Broker / finance" },
    { ref: "23", document: "List of all current open files", link: { kind: "report", report: "case-audit-report" }, notes: "Case Audit Report + per-case ledger balances (see gaps)" },
    { ref: "24", document: "Files legally completed in last 2 months", link: { kind: "report", report: "completion-report" }, notes: "Completion Report, filtered to the period" },
    { ref: "25", document: "List of all current ABS files", link: { kind: "report", report: "dual-rep-report" }, notes: "Dual Rep (ABS) Report" },
    { ref: "26", document: "ABS files completed in last 2 months", link: { kind: "report", report: "dual-rep-report" }, notes: "Dual Rep (ABS) Report ∩ completed" },
];

const ACCOUNTS_DOCS: Array<TDocRow> = [
    { ref: "1", document: "Client account reconciliations (4 months)", link: { kind: "external", key: "clientAccountReconciliations" }, notes: "Klyant — report + bank statement + cashbook" },
    { ref: "2", document: "Office account reconciliations (4 months)", link: { kind: "external", key: "officeAccountReconciliations" }, notes: "Xero — report + bank statement" },
    { ref: "3", document: "Designated deposit account statements", link: { kind: "external", key: "designatedDepositStatements" }, notes: "Bank / Klyant" },
    { ref: "4", document: "Client-to-client transfer list", link: { kind: "external", key: "clientToClientTransfers" }, notes: "Klyant" },
    { ref: "5", document: "Client-to-office transfer list", link: { kind: "external", key: "clientToOfficeTransfers" }, notes: "Klyant" },
    { ref: "6", document: "Office-to-client transfer list", link: { kind: "external", key: "officeToClientTransfers" }, notes: "Klyant" },
    { ref: "7", document: "Most recent P&L (incl. debtors & creditors)", link: { kind: "external", key: "profitAndLoss" }, notes: "Xero / accountant" },
    { ref: "8a", document: "Most recent balance sheet", link: { kind: "external", key: "balanceSheet" }, notes: "Xero / accountant" },
    { ref: "8b", document: "Latest accounts filed at Companies House", link: { kind: "external", key: "filedAccounts" }, notes: "Accountant" },
    { ref: "8c", document: "Trial balance", link: { kind: "external", key: "trialBalance" }, notes: "Xero / Klyant" },
    { ref: "9", document: "Aged balances list (dormant 12mo+)", link: { kind: "external", key: "agedBalancesList" }, notes: "Klyant — per-case ledgers cross-check on the day" },
    { ref: "10", document: "Record of aged balances withdrawn under £50", link: { kind: "external", key: "agedBalancesWithdrawnUnder50" }, notes: "Klyant" },
];

const POLICY_GUIDES: Array<{ label: string; slug: string }> = [
    { label: "AML policy", slug: "aml-policy" },
    { label: "Conflicts of Interest policy", slug: "conflicts-of-interest-policy" },
    { label: "Compliance policies index", slug: "compliance-policies-index" },
];

const KNOWN_GAPS_KEY = "known_gaps";

const KNOWN_GAPS: Array<string> = [
    "Source-of-funds / source-of-wealth blank checklist is a policy doc.",
    "SARs log (ISARs + DAML) maintained manually, not in the system.",
    "Account reconciliations & aged balances live in Klyant / Xero.",
    "\"Ceased to act\" / compliance disinstruction log is not structured.",
    "Open-files list has no single report with ledger balances — Case Audit Report is the closest.",
];

const ALL_DOCS: Array<TDocRow> = [...PART_C_DOCS, ...ACCOUNTS_DOCS];

const isRowLinked = (row: TDocRow, links: Record<string, string>): boolean =>
    row.link.kind === "report" || row.link.kind === "guide"
    || (row.link.kind === "external" && (links[row.link.key] ?? "") !== "");

const normaliseUrl = (url: string): string => {
    const trimmed = url.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const SectionHeading = (props: { children: React.ReactNode }): JSX.Element =>
    <FontQuicksand>
        <FontSize size={CRMFontSizes.LARGE}>
            <WeightBold>{props.children}</WeightBold>
        </FontSize>
    </FontQuicksand>;

const LinkCard = (props: { href: string; title: string; description?: string; cta: string }): JSX.Element =>
    <a className="crm-clc-audit__link-card" href={props.href} target="_blank" rel="noopener noreferrer">
        <div className="crm-clc-audit__link-card-title">{props.title}</div>
        {props.description !== undefined && <div className="crm-clc-audit__link-card-desc">{props.description}</div>}
        <div className="crm-clc-audit__link-card-cta">{props.cta}</div>
    </a>;

const ListRow = (props: { href: string; label: string }): JSX.Element =>
    <a className="crm-clc-audit__list-row" href={props.href} target="_blank" rel="noopener noreferrer">
        <span className="crm-clc-audit__list-row-label">{props.label}</span>
        <span className="crm-clc-audit__list-row-chevron">→</span>
    </a>;

const ExternalLinkCell = (props: { documentKey: string; url: string; onSaved: (url: string) => void }): JSX.Element => {
    const [editing, setEditing] = useState<boolean>(false);
    const [value, setValue] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);

    if (props.url !== "") {
        return <a className="crm-clc-audit__action-link" href={normaliseUrl(props.url)} target="_blank" rel="noopener noreferrer">Open ↗</a>;
    }

    if (!editing) {
        return <CRMButtonTertiary label="＋ Add link" onClick={() => setEditing(true)} />;
    }

    const save = (): void => {
        if (value.trim() === "" || saving) {
            return;
        }
        const normalised = normaliseUrl(value);
        setSaving(true);
        setFailed(false);
        fetch(`${env.REACT_APP_API_URL}/web/legal/clc-audit/document-links`, {
            method: "POST",
            credentials: "include",
            headers: { "accept": "application/json", "content-type": "application/json" },
            body: JSON.stringify({ document_key: props.documentKey, url: normalised }),
        })
            .then((res) => res.ok ? res.json() : Promise.reject(new Error(`save failed: ${res.status}`)))
            .then((raw: unknown) => {
                const decoded = CLCAuditDocumentLink.decode(raw);
                if (either.isRight(decoded)) {
                    props.onSaved(decoded.right.url);
                } else {
                    setSaving(false);
                    setFailed(true);
                }
            })
            .catch(() => { setSaving(false); setFailed(true); });
    };

    return (
        <span className="crm-clc-audit__addlink">
            <CRMFlatInputGeneral placeholder="Paste URL…" value={value} onChange={setValue} onPressEnterKey={save} />
            <CRMButtonTertiary label={saving ? "Saving…" : "Save"} onClick={save} disabled={saving || value.trim() === ""} />
            {failed && <span className="crm-clc-audit__addlink-error">Couldn’t save</span>}
        </span>
    );
};

const StatusPill = (props: { row: TDocRow; links: Record<string, string> }): JSX.Element => {
    if (props.row.link.kind === "report") { return <span className="crm-clc-audit__pill crm-clc-audit__pill--live">● Live</span>; }
    if (props.row.link.kind === "guide") { return <span className="crm-clc-audit__pill crm-clc-audit__pill--guide">◆ Guide</span>; }
    if (props.row.link.kind === "external") {
        return (props.links[props.row.link.key] ?? "") !== ""
            ? <span className="crm-clc-audit__pill crm-clc-audit__pill--ext">External</span>
            : <span className="crm-clc-audit__pill crm-clc-audit__pill--gap">＋ Needs link</span>;
    }
    return <span className="crm-clc-audit__pill crm-clc-audit__pill--ext">Document</span>;
};

const DocRegisterTable = (props: { rows: Array<TDocRow>; links: Record<string, string>; onSaved: (key: string, url: string) => void }): JSX.Element =>
    <div className="crm-report-table-wrap">
        <table className="crm-report-table">
            <thead>
                <tr>
                    <th className="crm-report-table__th">#</th>
                    <th className="crm-report-table__th">Document</th>
                    <th className="crm-report-table__th">Status</th>
                    <th className="crm-report-table__th">Where</th>
                </tr>
            </thead>
            <tbody>
                {props.rows.map((row) => {
                    const documentCell =
                        row.link.kind === "report" ? <a className="crm-clc-audit__action-link" href={`/crm/legal/reports?report=${row.link.report}`} target="_blank" rel="noopener noreferrer">{row.document}</a>
                        : row.link.kind === "guide" ? <a className="crm-clc-audit__action-link" href={`/crm/guides/legal/${row.link.slug}`} target="_blank" rel="noopener noreferrer">{row.document}</a>
                        : row.document;
                    const externalKey = row.link.kind === "external" ? row.link.key : null;
                    return (
                        <tr key={row.ref} className="crm-report-table__tr">
                            <td className="crm-report-table__td">{row.ref}</td>
                            <td className="crm-report-table__td">{documentCell}</td>
                            <td className="crm-report-table__td"><StatusPill row={row} links={props.links} /></td>
                            <td className="crm-report-table__td">
                                {externalKey !== null
                                    ? <ExternalLinkCell documentKey={externalKey} url={props.links[externalKey] ?? ""} onSaved={(url) => props.onSaved(externalKey, url)} />
                                    : <span className="crm-clc-audit__muted">{row.notes}</span>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>;

export const CRMLegalCLCAuditPage = (props: TContainerStateProps): JSX.Element => {
    const [summary, setSummary] = useState<TCLCAuditSummary | null>(null);
    const [links, setLinks] = useState<Record<string, string>>({});

    useEffect(() => {
        let cancelled = false;
        const headers = { "accept": "application/json", "content-type": "application/json" };

        fetch(`${env.REACT_APP_API_URL}/web/legal/clc-audit/summary`, { credentials: "include", headers })
            .then((res) => res.ok ? res.json() : Promise.reject(new Error(String(res.status))))
            .then((raw: unknown) => {
                const decoded = CLCAuditSummary.decode(raw);
                if (!cancelled && either.isRight(decoded)) { setSummary(decoded.right); }
            })
            .catch(() => { /* headline figures are non-critical */ });

        fetch(`${env.REACT_APP_API_URL}/web/legal/clc-audit/document-links`, { credentials: "include", headers })
            .then((res) => res.ok ? res.json() : Promise.reject(new Error(String(res.status))))
            .then((raw: unknown) => {
                const decoded = CLCAuditDocumentLinks.decode(raw);
                if (!cancelled && either.isRight(decoded)) {
                    setLinks(pipe(decoded.right.links, array.reduce({} as Record<string, string>, (acc, l) => ({ ...acc, [l.document_key]: l.url }))));
                }
            })
            .catch(() => { /* links are non-critical — rows fall back to "Add link" */ });

        return () => { cancelled = true; };
    }, []);

    const onSaved = (key: string, url: string): void => setLinks((prev) => ({ ...prev, [key]: url }));

    const savedGaps = links[KNOWN_GAPS_KEY];
    const gaps = savedGaps !== undefined && savedGaps.trim() !== ""
        ? savedGaps.split("\n").map((s) => s.trim()).filter((s) => s !== "")
        : KNOWN_GAPS;

    const [editingGaps, setEditingGaps] = useState<boolean>(false);
    const [gapsDraft, setGapsDraft] = useState<string>("");
    const [savingGaps, setSavingGaps] = useState<boolean>(false);
    const [gapsFailed, setGapsFailed] = useState<boolean>(false);

    const startEditingGaps = (): void => {
        setGapsDraft(gaps.join("\n"));
        setGapsFailed(false);
        setEditingGaps(true);
    };

    const saveGaps = (): void => {
        if (savingGaps) {
            return;
        }
        setSavingGaps(true);
        setGapsFailed(false);
        fetch(`${env.REACT_APP_API_URL}/web/legal/clc-audit/document-links`, {
            method: "POST",
            credentials: "include",
            headers: { "accept": "application/json", "content-type": "application/json" },
            body: JSON.stringify({ document_key: KNOWN_GAPS_KEY, url: gapsDraft }),
        })
            .then((res) => res.ok ? res.json() : Promise.reject(new Error(`save failed: ${res.status}`)))
            .then((raw: unknown) => {
                const decoded = CLCAuditDocumentLink.decode(raw);
                if (either.isRight(decoded)) {
                    onSaved(KNOWN_GAPS_KEY, decoded.right.url);
                    setSavingGaps(false);
                    setEditingGaps(false);
                } else {
                    setSavingGaps(false);
                    setGapsFailed(true);
                }
            })
            .catch(() => { setSavingGaps(false); setGapsFailed(true); });
    };

    const now = new Date();
    const daysToGo = Math.max(0, Math.ceil((Date.parse(INSPECTION_DATE_ISO) - now.getTime()) / (1000 * 60 * 60 * 24)));

    const linkedCount = ALL_DOCS.filter((row) => isRowLinked(row, links)).length;

    const stats: Array<{ label: string; value: number; warnWhenNonZero: boolean }> = summary === null ? [] : [
        { label: "Open files", value: summary.open_files, warnWhenNonZero: false },
        { label: "Completed (last 2 months)", value: summary.completed_last_2_months, warnWhenNonZero: false },
        { label: "ABS case pairs", value: summary.abs_case_pairs, warnWhenNonZero: false },
        { label: "Funders awaiting source of wealth", value: summary.funders_awaiting_source_of_wealth, warnWhenNonZero: true },
        { label: "Referral agreements overdue", value: summary.referral_agreements_overdue, warnWhenNonZero: true },
        { label: "Referral agreements missing", value: summary.referral_agreements_missing, warnWhenNonZero: true },
    ];

    return (
        <div className="crm-legal-cases">
            <CRMPageWrap>
                <CRMLegalMenuBarContainer {...props} />

                <CRMPageBody>
                    <CRMPadding size="large">
                        <CRMSpacingColumn spacing="large">

                            {/* TITLE + DATE CHIPS */}
                            <SpacingRow spacing={CRMSpacing.LARGE} justifyContent="space-between" alignItems="flex-start">
                                <CRMSpacingRow spacing="medium">
                                    <CRMIcon iconName="legal-gavel" size="large" colour="neutral-ink" />
                                    <CRMSpacingColumn spacing="tiny">
                                        <FontQuicksand>
                                            <FontSize size={CRMFontSizes.XX_LARGE}><WeightBold>CLC Monitoring Inspection</WeightBold></FontSize>
                                        </FontQuicksand>
                                        <FontSize size={CRMFontSizes.SMALL}>
                                            <CRMParagraph>Sail Legal · CLC-regulated · Code of Conduct · Conflicts · Complaints · Estimates &amp; TOE · AML/CTF · Accounts · Management</CRMParagraph>
                                        </FontSize>
                                    </CRMSpacingColumn>
                                </CRMSpacingRow>
                                <SpacingRow spacing={CRMSpacing.MEDIUM}>
                                    {[
                                        { n: `${daysToGo}`, l: "days to on-site" },
                                        { n: "22 Jul", l: "on-site" },
                                        { n: "~12 Jul", l: "Part C due" },
                                    ].map((chip, i) =>
                                        <CRMCardOutside key={i} borderRounding="right" shadow={true}>
                                            <CRMPadding size="medium">
                                                <CRMSpacingColumn spacing="tiny">
                                                    <FontQuicksand>
                                                        <FontSize size={CRMFontSizes.LARGE}>
                                                            <TextColor color={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0}><WeightBold>{chip.n}</WeightBold></TextColor>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                    <FontSize size={CRMFontSizes.X_SMALL}><CRMParagraph>{chip.l}</CRMParagraph></FontSize>
                                                </CRMSpacingColumn>
                                            </CRMPadding>
                                        </CRMCardOutside>
                                    )}
                                </SpacingRow>
                            </SpacingRow>

                            {/* HEADLINE FIGURES */}
                            {stats.length > 0 &&
                                <CRMSpacingColumn spacing="medium">
                                    <SectionHeading>Headline figures</SectionHeading>
                                    <SimpleGrid templateColumns="1fr 1fr 1fr 1fr 1fr 1fr" columnSpacing={CRMSpacing.MEDIUM} rowSpacing={CRMSpacing.MEDIUM}>
                                        {pipe(
                                            stats,
                                            array.mapWithIndex((i, stat) =>
                                                <CRMCardOutside key={i} borderRounding="right" shadow={true}>
                                                    <CRMPadding size="medium">
                                                        <CRMSpacingColumn spacing="tiny">
                                                            <FontQuicksand>
                                                                <FontSize size={CRMFontSizes.XX_LARGE}>
                                                                    <TextColor color={stat.warnWhenNonZero && stat.value > 0 ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 : CRMColors.NEUTRAL_INK}>
                                                                        <WeightBold>{stat.value}</WeightBold>
                                                                    </TextColor>
                                                                </FontSize>
                                                            </FontQuicksand>
                                                            <FontSize size={CRMFontSizes.X_SMALL}><CRMParagraph>{stat.label}</CRMParagraph></FontSize>
                                                        </CRMSpacingColumn>
                                                    </CRMPadding>
                                                </CRMCardOutside>
                                            )
                                        )}
                                    </SimpleGrid>
                                    <CRMSpacingColumn spacing="tiny">
                                        <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="space-between">
                                            <FontQuicksand><FontSize size={CRMFontSizes.SMALL}><WeightBold>Part C readiness</WeightBold></FontSize></FontQuicksand>
                                            <FontSize size={CRMFontSizes.SMALL}><CRMParagraph>{linkedCount} of {ALL_DOCS.length} documents linked</CRMParagraph></FontSize>
                                        </SpacingRow>
                                        <CRMProgressBar
                                            height="8px"
                                            progress={Math.round((linkedCount / ALL_DOCS.length) * 100)}
                                            fullBarColor={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2}
                                            emptyBarColor={CRMColors.NEUTRAL_8}
                                        />
                                    </CRMSpacingColumn>
                                </CRMSpacingColumn>
                            }

                            {/* MAIN + SIDEBAR */}
                            <CRMSpacingRow spacing="x-large" alignItems="flex-start" childSize="2fr 1fr">
                                <CRMSpacingColumn spacing="large">

                                    <SectionHeading>Management information reports</SectionHeading>
                                    {pipe(
                                        REPORT_GROUPS,
                                        array.map((group) =>
                                            <CRMSpacingColumn key={group.area} spacing="medium">
                                                <CRMSpacingColumn spacing="tiny">
                                                    <FontQuicksand>
                                                        <FontSize size={CRMFontSizes.MED}>
                                                            <TextColor color={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0}><WeightBold>{group.area}</WeightBold></TextColor>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                    <CRMParagraph>{group.blurb}</CRMParagraph>
                                                </CRMSpacingColumn>
                                                <SimpleGrid templateColumns="1fr 1fr" columnSpacing={CRMSpacing.MEDIUM} rowSpacing={CRMSpacing.MEDIUM}>
                                                    {pipe(
                                                        group.reports,
                                                        array.map((report) => {
                                                            const [title, description] = getReportTitleAndDescription(report);
                                                            return <LinkCard key={report} href={`/crm/legal/reports?report=${report}`} title={title} description={description} cta="Open report →" />;
                                                        })
                                                    )}
                                                </SimpleGrid>
                                            </CRMSpacingColumn>
                                        )
                                    )}

                                    <SectionHeading>Part C — documentation</SectionHeading>
                                    <DocRegisterTable rows={PART_C_DOCS} links={links} onSaved={onSaved} />

                                    <SectionHeading>Part C — accounts documentation</SectionHeading>
                                    <DocRegisterTable rows={ACCOUNTS_DOCS} links={links} onSaved={onSaved} />
                                </CRMSpacingColumn>

                                <Sticky top={CRMSpacing.X_LARGE}>
                                    <CRMSpacingColumn spacing="medium">
                                        <CRMCardOutside borderRounding="right" shadow={true}>
                                            <CRMPadding size="large">
                                                <CRMSpacingColumn spacing="tiny">
                                                    <FontQuicksand><FontSize size={CRMFontSizes.MED}><WeightBold>Key dates</WeightBold></FontSize></FontQuicksand>
                                                    <CRMParagraph>On-site inspection — <WeightBold>22 July 2026</WeightBold></CRMParagraph>
                                                    <CRMParagraph>Part C documents due — <WeightBold>~12 July 2026</WeightBold></CRMParagraph>
                                                    <CRMParagraph>Accounts period — <WeightBold>4 months</WeightBold></CRMParagraph>
                                                </CRMSpacingColumn>
                                            </CRMPadding>
                                        </CRMCardOutside>

                                        <CRMCardOutside borderRounding="right" shadow={true}>
                                            <CRMPadding size="large">
                                                <CRMSpacingColumn spacing="tiny">
                                                    <FontQuicksand><FontSize size={CRMFontSizes.MED}><WeightBold>Compliance policies</WeightBold></FontSize></FontQuicksand>
                                                    {pipe(
                                                        POLICY_GUIDES,
                                                        array.map((guide) =>
                                                            <ListRow key={guide.slug} href={`/crm/guides/legal/${guide.slug}`} label={guide.label} />
                                                        )
                                                    )}
                                                </CRMSpacingColumn>
                                            </CRMPadding>
                                        </CRMCardOutside>

                                        <CRMCardOutside borderRounding="right" shadow={true} backgroundColor="neutral-12">
                                            <CRMPadding size="large">
                                                <CRMSpacingColumn spacing="tiny">
                                                    <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="space-between">
                                                        <FontQuicksand><FontSize size={CRMFontSizes.MED}><WeightBold>Known gaps</WeightBold></FontSize></FontQuicksand>
                                                        {!editingGaps && <CRMButtonTertiary label="Edit" onClick={startEditingGaps} />}
                                                    </SpacingRow>
                                                    {editingGaps
                                                        ? <CRMSpacingColumn spacing="tiny">
                                                            <CRMTextAreaComponent value={gapsDraft} placeholder="One gap per line…" onChange={setGapsDraft} />
                                                            <SpacingRow spacing={CRMSpacing.TINY}>
                                                                <CRMButtonTertiary label={savingGaps ? "Saving…" : "Save"} onClick={saveGaps} disabled={savingGaps} />
                                                                <CRMButtonTertiary label="Cancel" onClick={() => setEditingGaps(false)} />
                                                            </SpacingRow>
                                                            {gapsFailed && <FontSize size={CRMFontSizes.SMALL}><CRMParagraph>Couldn’t save — try again.</CRMParagraph></FontSize>}
                                                        </CRMSpacingColumn>
                                                        : pipe(
                                                            gaps,
                                                            array.mapWithIndex((i, gap) =>
                                                                <SpacingRow key={i} spacing={CRMSpacing.TINY} alignItems="flex-start">
                                                                    <CRMIcon iconName="flag" size="tiny" colour="highlights-instructional-brick-0" />
                                                                    <FontSize size={CRMFontSizes.SMALL}><CRMParagraph>{gap}</CRMParagraph></FontSize>
                                                                </SpacingRow>
                                                            )
                                                        )
                                                    }
                                                </CRMSpacingColumn>
                                            </CRMPadding>
                                        </CRMCardOutside>
                                    </CRMSpacingColumn>
                                </Sticky>
                            </CRMSpacingRow>

                        </CRMSpacingColumn>
                    </CRMPadding>
                </CRMPageBody>
            </CRMPageWrap>
        </div>
    );
};
